import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  FeatherLoader,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import { UTCFormatDateShort, getProgramDatesRange } from '../../utils/date'
import ExperienceBadge from './ExperienceBadge'
import {
  getListStatusMeta,
  isQuestPublished,
  normalizeReadiness,
} from './experienceStatus'

const emptyFilter = { search: '' }
const PAGE_LIMIT = 25

const SORTABLE_COLUMNS = [
  { key: 'name', label: 'Experience Name', defaultOrder: 'asc' },
  { key: 'startDate', label: 'Start Date', defaultOrder: 'asc' },
  { key: 'endDate', label: 'End Date' },
  { key: 'status', label: 'Status', defaultOrder: 'asc' },
  { key: 'readiness', label: 'Readiness' },
  { key: 'participants', label: 'Participants', defaultOrder: 'desc' },
]

const COLUMN_WIDTHS = ['34%', '10%', '9%', '11%', '16%', '12%', 'auto']

const asRowArray = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.quests) ? data.quests : []

const getLeaderLabel = (quest) => {
  if (quest?.questLeaderName || quest?.leaderName) {
    return quest.questLeaderName || quest.leaderName
  }
  const emails = quest?.questLeadersEmails
  if (Array.isArray(emails) && emails.length > 0) {
    const first = emails[0]
    return typeof first === 'string' ? first : first?.value || first?.email
  }
  return null
}

const getSubtitle = (quest) =>
  [quest.programType, quest.location, getLeaderLabel(quest)]
    .filter(Boolean)
    .join(' · ')

/**
 * List readiness pills from API:
 * readiness: { contentIssues, enrollmentIssues, participantsPrepIssues }
 * (also accepts nested prosperity/enrollment/prep shape)
 */
const getReadinessPills = (quest) => {
  const r = normalizeReadiness(quest?.readiness)
  const pill = (key, label, item) => {
    if (!item) {
      return null
    }
    if (item.ok === true || item.complete === true) {
      return { key, text: `${label} ✓`, needs: false }
    }
    const n = item.issues ?? item.count ?? item.missing
    if (n != null && n > 0) {
      return { key, text: `${label} ${n}`, needs: true }
    }
    return null
  }

  if (r) {
    const pills = [
      pill('prosperity', 'Prosperity', r.prosperity),
      pill('enrollment', 'Enroll', r.enrollment),
      pill('prep', 'Prep', r.prep),
    ].filter(Boolean)
    // Drafts only surface open issues (prep is later and omitted when clear).
    if (!isQuestPublished(quest)) {
      return pills.filter((p) => p.needs)
    }
    return pills
  }

  // Fallback when API readiness is absent
  if (!isQuestPublished(quest)) {
    return [
      { key: 'prosperity', text: 'Prosperity', needs: true },
      { key: 'enrollment', text: 'Enroll', needs: true },
    ]
  }
  const status = getListStatusMeta(quest)
  if (status.key === 'done') {
    return []
  }
  return [
    { key: 'prosperity', text: 'Prosperity ✓', needs: false },
    { key: 'enrollment', text: 'Enroll ✓', needs: false },
    { key: 'prep', text: 'Prep', needs: true },
  ]
}

const getParticipantLabel = (quest) => {
  const count =
    quest?.participantsCount ??
    quest?.participantCount ??
    quest?.totalParticipants ??
    quest?.participants?.length
  if (count == null) {
    return '—'
  }
  const n = Number(count)
  if (Number.isNaN(n)) {
    return '—'
  }
  return `${n} participant${n === 1 ? '' : 's'}`
}

/** Same Imgix path/size as Header Image `.experience-img-mini` preview. */
const getListThumbStyle = (quest) => {
  const fileName = quest?.imageFileName || quest?.questImage
  if (!fileName || String(fileName).startsWith('data:')) {
    return undefined
  }
  return {
    backgroundImage: `url(${process.env.REACT_APP_IMG_SOURCE}/quest/${fileName}?ar=3:2&fit=crop&crop=center&w=88&auto=format)`,
  }
}

const CreatedQuestsTable = ({ isArchivedTable = false }) => {
  const history = useHistory()
  const { pathname } = useLocation()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const hasMoreRef = useRef(false)
  const fetchInFlightRef = useRef(false)
  const loaderRef = useRef(null)
  const observerRef = useRef(null)
  const searchDebounceRef = useRef(null)

  const [questList, setQuestList] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [globalFilter, setGlobalFilter] = useState(emptyFilter)
  const [sort, setSort] = useState({
    sortBy: 'startDate',
    sortOrder: 'asc',
  })
  const [query, setQuery] = useState(null)
  const [archivingId, setArchivingId] = useState(null)

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }
    searchDebounceRef.current = setTimeout(() => {
      setGlobalFilter({ search: searchInput.trim() })
    }, 300)
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [searchInput])

  useEffect(() => {
    setQuestList([])
    setHasMore(false)
    hasMoreRef.current = false
    setQuery({
      page: 1,
      search: globalFilter.search || '',
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    })
  }, [pathname, isArchivedTable, globalFilter.search, sort])

  useEffect(() => {
    if (!query) {
      return undefined
    }

    let cancelled = false
    const append = query.page > 1
    fetchInFlightRef.current = true
    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
    }

    ;(async () => {
      try {
        const params = {
          isArchived: isArchivedTable,
          page: query.page,
          limit: PAGE_LIMIT,
        }
        if (query.search) {
          params.name = query.search
        }
        if (query.sortBy) {
          params.sortBy = query.sortBy
          params.sortOrder = query.sortOrder
        }
        const { data } = await API.AdminAPI.quests.getQuestList(params)
        if (cancelled) {
          return
        }
        const list = asRowArray(data)
        const more = list.length === PAGE_LIMIT
        hasMoreRef.current = more
        setHasMore(more)
        if (append) {
          setQuestList((prev) => {
            const ids = new Set(prev.map((q) => q._id || q.id))
            return [...prev, ...list.filter((q) => !ids.has(q._id || q.id))]
          })
        } else {
          setQuestList(list)
        }
      } catch (e) {
        if (!cancelled) {
          Error.showError(e)
        }
      } finally {
        fetchInFlightRef.current = false
        if (!cancelled) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isArchivedTable])

  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore) {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      return undefined
    }
    const el = loaderRef.current
    if (!el) {
      return undefined
    }
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (
          target.isIntersecting &&
          hasMoreRef.current &&
          !fetchInFlightRef.current
        ) {
          setQuery((prev) => (prev ? { ...prev, page: prev.page + 1 } : prev))
        }
      },
      { root: null, rootMargin: '20px', threshold: 1.0 },
    )
    observerRef.current.observe(el)
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasMore, isLoading, isLoadingMore, questList.length])

  const openExperience = (quest) => {
    const id = quest?._id || quest?.id
    if (!isQuestPublished(quest)) {
      history.push(`/admin/experiences/${id}/build/setup-name`)
      return
    }
    history.push(`/admin/experiences/${id}/overview`)
  }

  const archiveExperience = async (quest) => {
    const id = quest?._id || quest?.id
    if (archivingId) {
      return
    }
    setArchivingId(id)
    try {
      await API.AdminAPI.quests.archiveQuest(id)
      setQuestList((prev) => prev.filter((q) => (q._id || q.id) !== id))
      Notification.showNotification('Experience has been archived!', 'success')
    } catch (e) {
      Error.showError(e)
    } finally {
      setArchivingId(null)
    }
  }

  const colSpan = 7

  const toggleSort = (column) => {
    setSort((prev) => {
      if (prev.sortBy === column.key) {
        return {
          sortBy: column.key,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        }
      }
      return { sortBy: column.key, sortOrder: column.defaultOrder }
    })
  }

  const getAriaSort = (column) => {
    if (sort.sortBy !== column.key) {
      return 'none'
    }
    return sort.sortOrder === 'asc' ? 'ascending' : 'descending'
  }

  const showEmpty = !isLoading && questList.length === 0

  return (
    <div className='experience-manage-list'>
      <div className='experience-tools'>
        <div className='experience-search'>
          <input
            type='search'
            placeholder='Search experiences…'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label='Search experiences'
          />
        </div>
      </div>

      {showEmpty ? (
        <div className='experience-pay-empty'>
          {isArchivedTable
            ? 'There are no experiences found.'
            : 'There are currently no experiences found. Please check back later or add a new experience.'}
        </div>
      ) : (
        <div className='experience-tbl-wrap experience-list-tbl-wrap'>
          <table
            className='experience-tbl'
            style={{ minWidth: 760, tableLayout: 'fixed' }}>
            <colgroup>
              {COLUMN_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {SORTABLE_COLUMNS.map((column) =>
                  column.defaultOrder ? (
                    <th key={column.key} aria-sort={getAriaSort(column)}>
                      <button
                        type='button'
                        className='experience-sort-btn'
                        onClick={() => toggleSort(column)}>
                        {column.label}
                        <svg
                          className={`experience-sort-icon${
                            sort.sortBy === column.key ? ' is-active' : ''
                          }`}
                          width='10'
                          height='10'
                          viewBox='0 0 10 10'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='1.6'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          aria-hidden='true'>
                          <path
                            d={
                              sort.sortBy === column.key &&
                              sort.sortOrder === 'desc'
                                ? 'M2 3.5L5 6.5L8 3.5'
                                : 'M2 6.5L5 3.5L8 6.5'
                            }
                          />
                        </svg>
                      </button>
                    </th>
                  ) : (
                    <th key={column.key}>{column.label}</th>
                  ),
                )}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && questList.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className='text-center py-3'>
                    <FeatherLoader />
                  </td>
                </tr>
              )}
              {questList.length > 0 &&
                !isLoading &&
                questList.map((quest) => {
                  const { start, end } = getProgramDatesRange(quest)
                  const status = getListStatusMeta(quest)
                  const subtitle = getSubtitle(quest)
                  const pills = getReadinessPills(quest)
                  const participants = getParticipantLabel(quest)
                  const hideReadiness = status.key === 'done'

                  return (
                    <tr
                      key={quest._id || quest.id}
                      className='experience-exp-row'
                      onClick={() => openExperience(quest)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openExperience(quest)
                        }
                      }}
                      tabIndex={0}
                      role='button'>
                      <td className='experience-who'>
                        <div className='experience-who-inner'>
                          <span
                            className={`experience-list-thumb${
                              quest.imageFileName || quest.questImage
                                ? ''
                                : ' is-empty'
                            }`}
                            style={getListThumbStyle(quest)}
                            aria-hidden='true'
                          />
                          <span className='experience-who-text'>
                            <b>{quest.name || quest.title || '—'}</b>
                            {subtitle ? <span>{subtitle}</span> : null}
                          </span>
                        </div>
                      </td>
                      <td>{UTCFormatDateShort(start) || '—'}</td>
                      <td>{UTCFormatDateShort(end) || '—'}</td>
                      <td>
                        <ExperienceBadge
                          title={status.label}
                          variant={status.variant}
                        />
                      </td>
                      <td className='experience-readiness-cell'>
                        {hideReadiness ? null : pills && pills.length > 0 ? (
                          <div className='experience-readiness-pills'>
                            {pills.map((p) => (
                              <span
                                key={p.key}
                                className={p.needs ? 'needs-txt' : 'ok-txt'}>
                                {p.text}
                              </span>
                            ))}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{participants}</td>
                      <td
                        className='experience-action-cell'
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}>
                        {!isArchivedTable && (
                          <button
                            type='button'
                            className='experience-archive-btn'
                            disabled={archivingId === (quest._id || quest.id)}
                            onClick={() => archiveExperience(quest)}>
                            {archivingId === (quest._id || quest.id)
                              ? 'Archiving…'
                              : 'Archive'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              {isLoadingMore && (
                <tr>
                  <td colSpan={colSpan} className='text-center py-2'>
                    <FeatherLoader />
                  </td>
                </tr>
              )}
            </tbody>
            {hasMore && questList.length >= PAGE_LIMIT && !isLoading ? (
              <tfoot>
                <tr>
                  <td colSpan={colSpan} style={{ border: 0, padding: 0 }}>
                    <div ref={loaderRef} style={{ height: 1 }} />
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </div>
  )
}

export default CreatedQuestsTable
